
import React from 'react';
import { Switch } from '@/components/ui/switch';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Eye, EyeOff, Trash2, AlertTriangle } from 'lucide-react';
import { SignupFormField } from '@/schemas/signupFormSchema';

interface FormFieldCardProps {
  field: SignupFormField;
  onUpdate: (updatedField: SignupFormField) => void;
  onDelete: () => void;
}

const FormFieldCard = ({
  field,
  onUpdate,
  onDelete
}: FormFieldCardProps) => {
  const handleToggleVisibility = () => {
    onUpdate({
      ...field,
      visible: !field.visible,
      required: !field.visible ? field.required : false
    });
  };

  const handleToggleRequired = () => {
    onUpdate({
      ...field,
      required: !field.required
    });
  };

  const handleLabelChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onUpdate({
      ...field,
      label: e.target.value
    });
  };

  const isCorefield = ['name', 'email', 'password'].includes(field.name);

  return (
    <Card className={!field.visible ? "border-dashed opacity-70" : ""}>
      <CardHeader className="pb-2 flex flex-row items-start justify-between">
        <div>
          <CardTitle className="text-sm font-medium flex items-center">
            {field.label}
            {field.required && <Badge className="ml-2 bg-red-500">Required</Badge>}
            {!field.visible && <Badge className="ml-2 bg-gray-500">Hidden</Badge>}
          </CardTitle>
          <CardDescription>
            Field type: {field.type} | Name: {field.name}
          </CardDescription>
        </div>
        {!isCorefield && (
          <Button 
            variant="ghost" 
            size="icon" 
            onClick={onDelete}
            className="h-8 w-8 text-red-600 hover:text-red-700"
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        )}
      </CardHeader>
      <CardContent>
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-2">
              <Switch
                checked={field.visible}
                onCheckedChange={handleToggleVisibility}
                id={`visible-${field.id}`}
              />
              <label htmlFor={`visible-${field.id}`} className="text-sm">
                {field.visible ? (
                  <span className="flex items-center text-green-600">
                    <Eye className="mr-1 h-4 w-4" /> Visible
                  </span>
                ) : (
                  <span className="flex items-center text-gray-500">
                    <EyeOff className="mr-1 h-4 w-4" /> Hidden
                  </span>
                )}
              </label>
            </div>
            
            <div className="flex items-center space-x-2">
              <Switch
                checked={field.required}
                onCheckedChange={handleToggleRequired}
                id={`required-${field.id}`}
                disabled={!field.visible || field.type === 'hidden'}
              />
              <label htmlFor={`required-${field.id}`} className="text-sm flex items-center">
                {field.required ? (
                  <span className="flex items-center text-red-600">
                    <AlertTriangle className="mr-1 h-4 w-4" /> Required
                  </span>
                ) : (
                  <span className="flex items-center text-gray-500">
                    Optional
                  </span>
                )}
              </label>
            </div>
          </div>
          
          <Input 
            className="w-40"
            value={field.label}
            onChange={handleLabelChange}
            placeholder="Field label"
          />
        </div>
      </CardContent>
    </Card>
  );
};

export default FormFieldCard;
